import { CloseCircleOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Checkbox,
  Col,
  ConfigProvider,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Switch,
  Table,
  Tag,
  TimePicker,
  Typography,
  notification,
} from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import commitPendingInput from "../../utils/commitPendingInput";
import useApi from "../../utils/useApi";

dayjs.extend(utc);

const { Text } = Typography;
const { Option } = Select;

const DAYS_OF_WEEK = [
  "Domingo",
  "Segunda",
  "Terça",
  "Quarta",
  "Quinta",
  "Sexta",
  "Sábado",
];

const mapDayToNumber = {
  Segunda: 0,
  Terça: 1,
  Quarta: 2,
  Quinta: 3,
  Sexta: 4,
  Sábado: 5,
  Domingo: 6,
};

const initializeStandardHours = () => {
  const initial = {};
  DAYS_OF_WEEK.forEach((day) => {
    initial[day] = {
      opening: null,
      closing: null,
      closed: false,
    };
  });
  return initial;
};

const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

export default function OpeningHours() {
  const {
    postStandardSchedule,
    postSpecialSchedule,
    getWeeklySchedule,
    postRevenueGoal,
    getRevenueGoals,
    deleteRevenueGoal,
    getRevenueGoalSellers,
    bulkUpdateRevenueGoalSellers,
    getNonSaleReasons,
    postNonSaleReason,
    patchNonSaleReason,
    deleteNonSaleReason,
  } = useApi();

  const { user } = useAuth();
  const isAdmin = Boolean(user?.is_superuser) || Boolean(user?.is_client_admin);
  const canManageReasons =
    isAdmin || Boolean(user?.is_admin) || Boolean(user?.is_staff);

  const [weeklySchedule, setWeeklySchedule] = useState([]);
  const [revenueGoals, setRevenueGoals] = useState([]);
  const [referenceDate, setReferenceDate] = useState(dayjs());
  const [goalMonth, setGoalMonth] = useState(dayjs().startOf("month"));
  const [goalAmount, setGoalAmount] = useState(null);
  const [isSavingGoal, setIsSavingGoal] = useState(false);
  const [editingGoalId, setEditingGoalId] = useState(null);
  const [sellerAllocations, setSellerAllocations] = useState([]);
  const [isLoadingSellers, setIsLoadingSellers] = useState(false);

  const [showStandardModal, setShowStandardModal] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [standardHours, setStandardHours] = useState(initializeStandardHours);
  const [dayToCopy, setDayToCopy] = useState(null);
  const [customDate, setCustomDate] = useState(null);
  const [customOpening, setCustomOpening] = useState(null);
  const [customClosing, setCustomClosing] = useState(null);
  const [customClosed, setCustomClosed] = useState(false);

  const valoresPendentesRef = useRef({});
  valoresPendentesRef.current = {
    standardHours,
    customDate,
    customOpening,
    customClosing,
    customClosed,
  };

  // ── Motivos de não venda ─────────────────────────────────────────────────
  const [reasons, setReasons] = useState([]);
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [editingReason, setEditingReason] = useState(null);
  const [isSavingReason, setIsSavingReason] = useState(false);
  const [reasonForm] = Form.useForm();

  // ── Revenue goal year grouping ────────────────────────────────────────────
  const [expandedYears, setExpandedYears] = useState(new Set());

  useEffect(() => {
    fetchWeeklySchedule(referenceDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceDate]);

  useEffect(() => {
    loadRevenueGoals();
    loadReasons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortedRevenueGoals = useMemo(
    () =>
      [...revenueGoals].sort(
        (a, b) =>
          dayjs(b.reference_month).valueOf() -
          dayjs(a.reference_month).valueOf()
      ),
    [revenueGoals]
  );

  const sellerAllocationsSum = useMemo(
    () =>
      sellerAllocations.reduce(
        (acc, s) => acc + Number(s.target_amount || 0),
        0
      ),
    [sellerAllocations]
  );

  const selectedGoalForMonth = useMemo(
    () =>
      revenueGoals.find(
        (goal) =>
          dayjs(goal.reference_month).format("YYYY-MM") ===
          goalMonth.format("YYYY-MM")
      ) || null,
    [goalMonth, revenueGoals]
  );

  useEffect(() => {
    setGoalAmount(
      selectedGoalForMonth && selectedGoalForMonth.target_amount !== null
        ? Number(selectedGoalForMonth.target_amount)
        : null
    );
  }, [selectedGoalForMonth]);

  const fetchWeeklySchedule = async (date) => {
    try {
      const dateToFetch = date || dayjs();
      const response = await getWeeklySchedule(
        dateToFetch.format("YYYY-MM-DD")
      );
      const data = response.weekly_schedule || [];

      const mappedData = data.map((item) => {
        const parsedDate = dayjs(item.date);
        return {
          ...item,
          weekday: DAYS_OF_WEEK[parsedDate.day()],
        };
      });

      setWeeklySchedule(mappedData);
    } catch (err) {
      notification.error({
        message: "Erro ao buscar horários",
        description: err.message,
      });
    }
  };

  const loadRevenueGoals = async () => {
    try {
      const response = await getRevenueGoals();
      setRevenueGoals(Array.isArray(response) ? response : []);
    } catch (error) {
      notification.error({
        message: "Erro ao buscar metas de faturamento",
        description: error?.message,
      });
    }
  };

  const loadReasons = async () => {
    try {
      const data = await getNonSaleReasons();
      setReasons(Array.isArray(data) ? data : []);
    } catch (error) {
      notification.error({
        message: "Erro ao buscar motivos de não venda",
        description: error?.message,
      });
    }
  };

  const handleOpenReasonModal = (reason = null) => {
    setEditingReason(reason);
    if (reason) {
      reasonForm.setFieldsValue({
        code: reason.code,
        description: reason.description,
        is_active: reason.is_active,
      });
    } else {
      reasonForm.resetFields();
      reasonForm.setFieldsValue({ is_active: true });
    }
    setReasonModalOpen(true);
  };

  const handleSaveReason = async () => {
    let values;
    try {
      values = await reasonForm.validateFields();
    } catch {
      return;
    }
    try {
      setIsSavingReason(true);
      if (editingReason) {
        await patchNonSaleReason(editingReason.id, values);
      } else {
        const clientId = user?.available_branches?.[0]?.client_id ?? undefined;
        await postNonSaleReason(
          clientId ? { ...values, client_id: clientId } : values
        );
      }
      notification.success({
        message: editingReason
          ? "Motivo atualizado com sucesso."
          : "Motivo cadastrado com sucesso.",
      });
      setReasonModalOpen(false);
      await loadReasons();
    } catch (error) {
      notification.error({
        message: "Erro ao salvar motivo",
        description: error?.response?.data?.detail || error?.message,
      });
    } finally {
      setIsSavingReason(false);
    }
  };

  const handleDeleteReason = async (id) => {
    try {
      await deleteNonSaleReason(id);
      notification.success({ message: "Motivo excluído com sucesso." });
      await loadReasons();
    } catch (error) {
      notification.error({
        message: "Não foi possível excluir",
        description: error?.response?.data?.detail || error?.message,
      });
    }
  };

  // ── Year-grouped revenue goals ─────────────────────────────────────────────

  const goalsByYear = useMemo(() => {
    const map = {};
    sortedRevenueGoals.forEach((goal) => {
      const year = dayjs(goal.reference_month).year();
      if (!map[year]) map[year] = [];
      map[year].push(goal);
    });
    return map;
  }, [sortedRevenueGoals]);

  const sortedYears = useMemo(
    () =>
      Object.keys(goalsByYear)
        .map(Number)
        .sort((a, b) => b - a),
    [goalsByYear]
  );

  const toggleYear = (year) => {
    setExpandedYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) {
        next.delete(year);
      } else {
        next.add(year);
      }
      return next;
    });
  };

  const handlePreviousWeek = () =>
    setReferenceDate((prev) => prev.subtract(7, "day"));
  const handleNextWeek = () => setReferenceDate((prev) => prev.add(7, "day"));

  const handleSaveStandard = async () => {
    await commitPendingInput();
    const horarios = valoresPendentesRef.current.standardHours;
    try {
      for (const day of DAYS_OF_WEEK) {
        const { opening, closing, closed } = horarios[day];
        if (!closed && opening && closing) {
          const openTime = dayjs(opening, "HH:mm");
          const closeTime = dayjs(closing, "HH:mm");

          if (closeTime.isBefore(openTime)) {
            notification.warning({
              message: "Horário inválido",
              description: `Nos dias referentes à ${day}, o horário de fechamento não pode ser menor que o de abertura.`,
            });
            return;
          }
        }
      }

      for (const day of DAYS_OF_WEEK) {
        const { opening, closing, closed } = horarios[day];
        const dayNumber = mapDayToNumber[day];

        if (closed) {
          await postStandardSchedule({
            day_of_week: dayNumber,
            open_time: "00:00",
            close_time: "00:00",
          });
        } else if (opening && closing) {
          await postStandardSchedule({
            day_of_week: dayNumber,
            open_time: opening,
            close_time: closing,
          });
        }
      }

      notification.success({ message: "Horários padrão salvos com sucesso." });
      setShowStandardModal(false);
      await fetchWeeklySchedule(referenceDate);
    } catch (error) {
      notification.error({
        message: "Erro ao salvar horários padrão",
        description: error?.message,
      });
    }
  };

  const handleSaveCustom = async () => {
    await commitPendingInput();
    const {
      customDate: dataAtual,
      customOpening: aberturaAtual,
      customClosing: fechamentoAtual,
      customClosed: fechadoAtual,
    } = valoresPendentesRef.current;

    if (!dataAtual) {
      notification.warning({ message: "Selecione uma data." });
      return;
    }

    let finalOpening = aberturaAtual;
    let finalClosing = fechamentoAtual;

    if (fechadoAtual) {
      finalOpening = "00:00";
      finalClosing = "00:00";
    } else {
      if (!aberturaAtual || !fechamentoAtual) {
        notification.warning({ message: "Selecione abertura e fechamento." });
        return;
      }

      const openTime = dayjs(aberturaAtual, "HH:mm");
      const closeTime = dayjs(fechamentoAtual, "HH:mm");
      if (closeTime.isBefore(openTime)) {
        notification.warning({
          message: "Horário inválido",
          description:
            "O horário de fechamento não pode ser menor que o de abertura.",
        });
        return;
      }
    }

    try {
      await postSpecialSchedule({
        date: dataAtual.format("YYYY-MM-DD"),
        open_time: finalOpening,
        close_time: finalClosing,
      });

      notification.success({
        message: "Horário personalizado salvo com sucesso.",
      });

      setShowCustomModal(false);
      setCustomDate(null);
      setCustomOpening(null);
      setCustomClosing(null);
      setCustomClosed(false);
      await fetchWeeklySchedule(referenceDate);
    } catch (error) {
      notification.error({
        message: "Erro ao salvar horário personalizado",
        description: error?.message,
      });
    }
  };

  const loadSellerAllocations = async (month) => {
    setIsLoadingSellers(true);
    try {
      const data = await getRevenueGoalSellers(
        month.startOf("month").format("YYYY-MM-DD")
      );
      setSellerAllocations(data.sellers || []);
    } catch (error) {
      notification.error({
        message: "Erro ao buscar vendedores",
        description: error?.message,
      });
      setSellerAllocations([]);
    } finally {
      setIsLoadingSellers(false);
    }
  };

  const handleSaveGoal = async () => {
    if (!goalMonth) {
      notification.warning({ message: "Selecione o mês e ano da meta." });
      return;
    }

    if (
      goalAmount === null ||
      goalAmount === undefined ||
      Number(goalAmount) <= 0
    ) {
      notification.warning({
        message: "Insira um valor maior que R$ 0.",
      });
      return;
    }

    if (sellerAllocations.length > 0) {
      if (Math.abs(sellerAllocationsSum - Number(goalAmount)) > 0.02) {
        notification.warning({
          message: "Soma das metas por vendedor não bate com a meta total.",
          description: `Total vendedores: ${currencyFormatter.format(sellerAllocationsSum)} / Meta: ${currencyFormatter.format(goalAmount)}`,
        });
        return;
      }
    }

    try {
      setIsSavingGoal(true);
      const monthStr = goalMonth.startOf("month").format("YYYY-MM-DD");
      await postRevenueGoal({
        reference_month: monthStr,
        target_amount: Number(goalAmount).toFixed(2),
      });

      if (sellerAllocations.length > 0) {
        await bulkUpdateRevenueGoalSellers(
          monthStr,
          sellerAllocations.map((s) => ({
            erp_code: s.erp_code,
            target_amount: Number(s.target_amount).toFixed(2),
          }))
        );
      }

      notification.success({
        message: "Meta de faturamento salva com sucesso.",
      });
      await loadRevenueGoals();
      setEditingGoalId(null);
      setSellerAllocations([]);
    } catch (error) {
      notification.error({
        message: "Erro ao salvar meta de faturamento",
        description: error?.message,
      });
    } finally {
      setIsSavingGoal(false);
    }
  };

  const handleEditGoal = (goal) => {
    setEditingGoalId(goal.id);
    const month = dayjs(goal.reference_month).startOf("month");
    setGoalMonth(month);
    setGoalAmount(Number(goal.target_amount || 0));
    loadSellerAllocations(month);
  };

  const handleCancelGoalEdit = () => {
    setEditingGoalId(null);
    setGoalMonth(dayjs().startOf("month"));
    setSellerAllocations([]);
  };

  const handleDeleteGoal = (goal) => {
    Modal.confirm({
      title: "Excluir meta",
      content: `Excluir meta de ${dayjs(goal.reference_month).format("MM/YYYY")}?`,
      okText: "Excluir",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await deleteRevenueGoal(goal.id);
          notification.success({ message: "Meta excluída com sucesso." });
          await loadRevenueGoals();
          if (editingGoalId === goal.id) {
            setEditingGoalId(null);
            setGoalMonth(dayjs().startOf("month"));
            setSellerAllocations([]);
          }
        } catch {
          notification.error({ message: "Erro ao excluir meta." });
        }
      },
    });
  };

  const handleSellerAmountChange = (erp_code, value) => {
    setSellerAllocations((prev) =>
      prev.map((s) =>
        s.erp_code === erp_code ? { ...s, target_amount: value ?? 0 } : s
      )
    );
  };

  const handleRedistribute = () => {
    if (!goalAmount || sellerAllocations.length === 0) return;
    const total = Number(goalAmount);
    const n = sellerAllocations.length;
    const base = Math.floor((total / n) * 100) / 100;
    const remainder = Math.round((total - base * n) * 100) / 100;
    setSellerAllocations((prev) =>
      prev.map((s, i) => ({
        ...s,
        target_amount: i === 0 ? base + remainder : base,
      }))
    );
  };

  const updateStandardHours = (day, isOpening, timeValue) => {
    setStandardHours((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [isOpening ? "opening" : "closing"]: timeValue,
      },
    }));
  };

  const toggleClosed = (day) => {
    setStandardHours((prev) => {
      const willClose = !prev[day].closed;
      return {
        ...prev,
        [day]: {
          ...prev[day],
          closed: willClose,
          opening: willClose ? "00:00" : null,
          closing: willClose ? "00:00" : null,
        },
      };
    });
  };

  const handleCopyHours = () => {
    if (!dayToCopy) return;
    const dayData = standardHours[dayToCopy];
    setStandardHours((prev) => {
      const newHours = { ...prev };
      DAYS_OF_WEEK.forEach((day) => {
        if (day !== dayToCopy) {
          newHours[day] = {
            opening: dayData.opening,
            closing: dayData.closing,
            closed: dayData.closed,
          };
        }
      });
      return newHours;
    });

    notification.info({
      message: `Horários copiados de ${dayToCopy} para todos os outros dias.`,
    });
  };

  const weeklyColumns = [
    {
      title: "Data",
      dataIndex: "date",
      key: "date",
      render: (value) => dayjs(value).format("DD-MM-YYYY"),
    },
    {
      title: "Dia da semana",
      dataIndex: "weekday",
      key: "weekday",
    },
    {
      title: "Abertura",
      dataIndex: "open_time",
      key: "open_time",
    },
    {
      title: "Fechamento",
      dataIndex: "close_time",
      key: "close_time",
    },
  ];

  const goalColumns = [
    {
      title: "Mês/Ano",
      dataIndex: "reference_month",
      key: "reference_month",
      render: (value) => dayjs(value).format("MM/YYYY"),
      sorter: (a, b) =>
        dayjs(a.reference_month).valueOf() - dayjs(b.reference_month).valueOf(),
      defaultSortOrder: "descend",
    },
    {
      title: "Meta de faturamento",
      dataIndex: "target_amount",
      key: "target_amount",
      align: "right",
      render: (value) => currencyFormatter.format(Number(value || 0)),
      sorter: (a, b) =>
        Number(a.target_amount || 0) - Number(b.target_amount || 0),
    },
    {
      title: "Ações",
      key: "actions",
      align: "center",
      width: 160,
      render: (_, record) => (
        <>
          <Button type="link" onClick={() => handleEditGoal(record)}>
            Editar
          </Button>
          <Button type="link" danger onClick={() => handleDeleteGoal(record)}>
            Excluir
          </Button>
        </>
      ),
    },
  ];

  return (
    <ConfigProvider
      theme={{
        components: {
          Card: {
            headerBg: "var(--ouran-card-header-bg)",
          },
        },
      }}>
      <div
        style={{
          width: "100%",
          marginTop: 20,
          display: "flex",
          justifyContent: "center",
        }}>
        <div
          style={{
            width: "90%",
            display: "flex",
            flexDirection: "column",
            gap: 20,
          }}>
          <Card title="Gerenciar horários de funcionamento da loja">
            <div
              style={{
                marginTop: 20,
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                flexWrap: "wrap",
              }}>
              <Text strong>Ver horários a partir de:</Text>
              <DatePicker
                value={referenceDate}
                format="DD-MM-YYYY"
                onChange={(date) => {
                  setReferenceDate(date || dayjs());
                }}
              />

              <Button type="default" onClick={() => setReferenceDate(dayjs())}>
                Hoje
              </Button>
            </div>

            <div
              style={{
                marginTop: 20,
                marginBottom: 20,
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
              }}>
              <Button type="primary" onClick={() => setShowStandardModal(true)}>
                Configurar horários padrão
              </Button>
              <Button type="default" onClick={() => setShowCustomModal(true)}>
                Adicionar horário personalizado
              </Button>
            </div>

            <Table
              columns={weeklyColumns}
              dataSource={weeklySchedule}
              rowKey="date"
              pagination={false}
            />

            <div
              style={{
                marginTop: 20,
                display: "flex",
                justifyContent: "flex-end",
                gap: 16,
              }}>
              <Button onClick={handlePreviousWeek}>{"<"}</Button>
              <Button onClick={handleNextWeek}>{">"}</Button>
            </div>
          </Card>

          <Card title="Gerenciar meta mensal de faturamento">
            <Row gutter={[16, 16]} align="bottom" style={{ marginBottom: 20 }}>
              <Col xs={24} md={8}>
                <Text strong>Mês/Ano da meta</Text>
                <div style={{ marginTop: 8 }}>
                  <DatePicker
                    picker="month"
                    value={goalMonth}
                    format="MM/YYYY"
                    style={{ width: "100%" }}
                    onChange={(date) =>
                      setGoalMonth(date || dayjs().startOf("month"))
                    }
                  />
                </div>
              </Col>
              <Col xs={24} md={8}>
                <Text strong>Meta de faturamento</Text>
                <div style={{ marginTop: 8 }}>
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    precision={2}
                    value={goalAmount}
                    onChange={setGoalAmount}
                    formatter={(value) =>
                      value === null || value === undefined || value === ""
                        ? ""
                        : `R$ ${String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`
                    }
                    parser={(value) =>
                      (() => {
                        const normalized = String(value || "")
                          .replace(/R\$\s?/g, "")
                          .replace(/\./g, "")
                          .replace(",", ".")
                          .trim();
                        if (!normalized) return null;
                        const parsed = Number(normalized);
                        return Number.isNaN(parsed) ? null : parsed;
                      })()
                    }
                    placeholder="Informe a meta do mês"
                  />
                </div>
              </Col>
              <Col xs={24} md={8}>
                {selectedGoalForMonth || editingGoalId ? (
                  <Text
                    type="secondary"
                    style={{ display: "block", marginBottom: 8 }}>
                    Editando a meta de {goalMonth.format("MM/YYYY")}.
                  </Text>
                ) : (
                  <div style={{ height: 30 }} />
                )}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Button
                    type="primary"
                    loading={isSavingGoal}
                    onClick={handleSaveGoal}>
                    {selectedGoalForMonth || editingGoalId
                      ? "Salvar alterações"
                      : "Salvar meta"}
                  </Button>
                  {selectedGoalForMonth || editingGoalId ? (
                    <Button onClick={handleCancelGoalEdit}>Cancelar</Button>
                  ) : null}
                </div>
              </Col>
            </Row>

            {editingGoalId && (
              <div style={{ marginBottom: 24 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 12,
                  }}>
                  <Text strong>Distribuição por vendedor</Text>
                  <Button size="small" onClick={handleRedistribute}>
                    Distribuir igualmente
                  </Button>
                  {sellerAllocations.length > 0 &&
                    (Math.abs(sellerAllocationsSum - Number(goalAmount || 0)) >
                    0.02 ? (
                      <Text type="danger" style={{ fontSize: 12 }}>
                        Soma: {currencyFormatter.format(sellerAllocationsSum)}{" "}
                        (faltam{" "}
                        {currencyFormatter.format(
                          Number(goalAmount || 0) - sellerAllocationsSum
                        )}
                        )
                      </Text>
                    ) : (
                      <Text type="success" style={{ fontSize: 12 }}>
                        ✓ {currencyFormatter.format(sellerAllocationsSum)}
                      </Text>
                    ))}
                </div>
                <Table
                  loading={isLoadingSellers}
                  size="small"
                  pagination={false}
                  dataSource={sellerAllocations.map((s) => ({
                    ...s,
                    key: s.erp_code,
                  }))}
                  locale={{ emptyText: "Nenhum vendedor ativo encontrado." }}
                  columns={[
                    {
                      title: "Vendedor",
                      dataIndex: "nome",
                      key: "nome",
                    },
                    {
                      title: "Cód. ERP",
                      dataIndex: "erp_code",
                      key: "erp_code",
                      width: 100,
                    },
                    {
                      title: "Meta",
                      dataIndex: "target_amount",
                      key: "target_amount",
                      align: "right",
                      width: 200,
                      render: (value, record) => (
                        <InputNumber
                          size="small"
                          style={{ width: "100%" }}
                          min={0}
                          precision={2}
                          value={Number(value)}
                          onChange={(v) =>
                            handleSellerAmountChange(record.erp_code, v)
                          }
                          formatter={(v) =>
                            v === null || v === undefined || v === ""
                              ? ""
                              : `R$ ${String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`
                          }
                          parser={(v) => {
                            const normalized = String(v || "")
                              .replace(/R\$\s?/g, "")
                              .replace(/\./g, "")
                              .replace(",", ".")
                              .trim();
                            if (!normalized) return 0;
                            const parsed = Number(normalized);
                            return Number.isNaN(parsed) ? 0 : parsed;
                          }}
                        />
                      ),
                    },
                  ]}
                />
              </div>
            )}

            {sortedYears.length === 0 && (
              <Text type="secondary">
                Nenhuma meta de faturamento cadastrada.
              </Text>
            )}
            {sortedYears.map((year) => {
              const expanded = expandedYears.has(year);
              const yearTotal = goalsByYear[year].reduce(
                (acc, g) => acc + Number(g.target_amount || 0),
                0
              );
              return (
                <div key={year} style={{ marginBottom: 8 }}>
                  <div
                    onClick={() => toggleYear(year)}
                    style={{
                      cursor: "pointer",
                      padding: "8px 12px",
                      background: "#f0f4f8",
                      borderRadius: 6,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      userSelect: "none",
                    }}>
                    <Text strong>
                      <span
                        style={{
                          display: "inline-block",
                          transform: expanded
                            ? "rotate(0deg)"
                            : "rotate(-90deg)",
                          color: "#000",
                          fontSize: 11,
                          marginRight: 6,
                        }}>
                        ▼
                      </span>
                      {year}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {goalsByYear[year].length} meses &mdash; Total:{" "}
                      {currencyFormatter.format(yearTotal)}
                    </Text>
                  </div>
                  {expanded && (
                    <Table
                      size="small"
                      columns={goalColumns}
                      dataSource={goalsByYear[year].map((g) => ({
                        ...g,
                        key: g.id,
                      }))}
                      pagination={false}
                      style={{ marginTop: 4 }}
                    />
                  )}
                </div>
              );
            })}
          </Card>

          <Card title="Motivos de Não Venda">
            {canManageReasons && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                style={{ marginBottom: 16 }}
                onClick={() => handleOpenReasonModal()}>
                Novo motivo
              </Button>
            )}
            <Table
              size="small"
              dataSource={reasons.map((r) => ({ ...r, key: r.id }))}
              pagination={false}
              locale={{ emptyText: "Nenhum motivo cadastrado." }}
              columns={[
                {
                  title: "Cód.",
                  dataIndex: "code",
                  key: "code",
                  width: 60,
                  render: (v) => String(v).padStart(2, "0"),
                },
                {
                  title: "Descrição",
                  dataIndex: "description",
                  key: "description",
                },
                {
                  title: "Ativo",
                  dataIndex: "is_active",
                  key: "is_active",
                  width: 80,
                  render: (v) => (
                    <Tag color={v ? "green" : "default"}>
                      {v ? "Sim" : "Não"}
                    </Tag>
                  ),
                },
                ...(canManageReasons
                  ? [
                      {
                        title: "Ações",
                        key: "actions",
                        width: 140,
                        render: (_, record) => (
                          <div style={{ display: "flex", gap: 8 }}>
                            <Button
                              size="small"
                              type="link"
                              onClick={() => handleOpenReasonModal(record)}>
                              Editar
                            </Button>
                            <Button
                              size="small"
                              type="link"
                              danger
                              onClick={() =>
                                Modal.confirm({
                                  title: "Excluir motivo?",
                                  content:
                                    "Esta ação não pode ser desfeita se o motivo não tiver registros vinculados.",
                                  okText: "Excluir",
                                  okButtonProps: { danger: true },
                                  cancelText: "Cancelar",
                                  onOk: () => handleDeleteReason(record.id),
                                })
                              }>
                              Excluir
                            </Button>
                          </div>
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          </Card>

          <Modal
            title={
              <span style={{ fontSize: 20, fontWeight: "bold" }}>
                Configurar horários padrão
              </span>
            }
            open={showStandardModal}
            onCancel={() => setShowStandardModal(false)}
            onOk={handleSaveStandard}
            okText="Salvar"
            cancelText="Cancelar">
            <Row style={{ marginBottom: 16 }} gutter={32}>
              <Col span={24}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Select
                    placeholder="Copiar horários de..."
                    style={{ width: 180 }}
                    onChange={(value) => setDayToCopy(value)}
                    value={dayToCopy}>
                    {DAYS_OF_WEEK.map((day) => (
                      <Option key={day} value={day}>
                        {day}
                      </Option>
                    ))}
                  </Select>
                  <Button
                    type="default"
                    onClick={handleCopyHours}
                    disabled={!dayToCopy}>
                    Copiar para todos
                  </Button>
                </div>
              </Col>
            </Row>

            <Row style={{ marginBottom: 24, marginTop: 24 }} gutter={32}>
              <Col span={6}>
                <Text strong>Dia da semana</Text>
              </Col>
              <Col span={8}>
                <Text strong>Abertura</Text>
              </Col>
              <Col span={8}>
                <Text strong>Fechamento</Text>
              </Col>
              <Col span={2}></Col>
            </Row>

            {DAYS_OF_WEEK.map((day) => (
              <Row key={day} style={{ marginBottom: 16 }} gutter={32}>
                <Col
                  span={6}
                  style={{ display: "flex", alignItems: "center", height: 32 }}>
                  <Text strong>{day}</Text>
                </Col>

                <Col span={8}>
                  <TimePicker
                    style={{ width: "100%" }}
                    value={
                      standardHours[day].opening
                        ? dayjs(standardHours[day].opening, "HH:mm")
                        : null
                    }
                    format="HH:mm"
                    needConfirm={false}
                    showNow={false}
                    onChange={(time, timeString) =>
                      updateStandardHours(day, true, timeString)
                    }
                    disabled={standardHours[day].closed}
                  />
                </Col>

                <Col span={8}>
                  <TimePicker
                    style={{ width: "100%" }}
                    value={
                      standardHours[day].closing
                        ? dayjs(standardHours[day].closing, "HH:mm")
                        : null
                    }
                    format="HH:mm"
                    needConfirm={false}
                    showNow={false}
                    onChange={(time, timeString) =>
                      updateStandardHours(day, false, timeString)
                    }
                    disabled={standardHours[day].closed}
                  />
                </Col>

                {(day === "Segunda" || day === "Domingo") && (
                  <Col
                    span={2}
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                    }}>
                    <CloseCircleOutlined
                      style={{
                        fontSize: 20,
                        color: standardHours[day].closed ? "red" : "#999",
                        cursor: "pointer",
                      }}
                      onClick={() => toggleClosed(day)}
                    />
                  </Col>
                )}
              </Row>
            ))}

            <Row style={{ color: "grey", marginTop: 25, marginBottom: 20 }}>
              Clique no X caso a loja não abra nos dias indicados.
            </Row>
          </Modal>

          <Modal
            title={
              <span style={{ fontSize: 18, fontWeight: "bold" }}>
                Adicionar horário personalizado
              </span>
            }
            open={showCustomModal}
            onCancel={() => setShowCustomModal(false)}
            onOk={handleSaveCustom}
            okText="Salvar"
            cancelText="Cancelar">
            <div style={{ marginTop: 20, marginBottom: 20 }}>
              <Row style={{ marginBottom: 24 }} gutter={24}>
                <Col span={24}>
                  <Text strong>Selecione a data:</Text>
                  <div style={{ marginTop: 8 }}>
                    <DatePicker
                      format="DD-MM-YYYY"
                      value={customDate}
                      onChange={(date) => setCustomDate(date)}
                    />
                  </div>
                </Col>
              </Row>

              <Row style={{ marginBottom: 24 }} gutter={24}>
                <Col span={12}>
                  <Text strong>Abertura:</Text>
                  <div style={{ marginTop: 8 }}>
                    <TimePicker
                      value={
                        customOpening ? dayjs(customOpening, "HH:mm") : null
                      }
                      format="HH:mm"
                      needConfirm={false}
                      showNow={false}
                      onChange={(time, timeString) =>
                        setCustomOpening(timeString)
                      }
                      disabled={customClosed}
                    />
                  </div>
                </Col>
                <Col span={12}>
                  <Text strong>Fechamento:</Text>
                  <div style={{ marginTop: 8 }}>
                    <TimePicker
                      value={
                        customClosing ? dayjs(customClosing, "HH:mm") : null
                      }
                      format="HH:mm"
                      needConfirm={false}
                      showNow={false}
                      onChange={(time, timeString) =>
                        setCustomClosing(timeString)
                      }
                      disabled={customClosed}
                    />
                  </div>
                </Col>
              </Row>

              <Row style={{ marginBottom: 24 }} gutter={24}>
                <Col span={24}>
                  <Checkbox
                    checked={customClosed}
                    onChange={(e) => setCustomClosed(e.target.checked)}>
                    Fechar a loja neste dia (00:00 às 00:00)
                  </Checkbox>
                </Col>
              </Row>
            </div>
          </Modal>
        </div>
      </div>

      <Modal
        title={editingReason ? "Editar motivo" : "Novo motivo de não venda"}
        open={reasonModalOpen}
        onCancel={() => setReasonModalOpen(false)}
        onOk={handleSaveReason}
        confirmLoading={isSavingReason}
        okText="Salvar"
        cancelText="Cancelar">
        <Form form={reasonForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="code"
            label="Código (01–99)"
            rules={[
              { required: true, message: "Informe o código." },
              {
                type: "number",
                min: 1,
                max: 99,
                message: "Deve ser entre 1 e 99.",
              },
            ]}>
            <InputNumber min={1} max={99} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="description"
            label="Descrição"
            rules={[{ required: true, message: "Informe a descrição." }]}>
            <Input />
          </Form.Item>
          <Form.Item name="is_active" label="Ativo" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </ConfigProvider>
  );
}
